import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEye, faEyeSlash, faKey } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpError } from 'src/app/core/http-error';
import { Authenticate } from 'src/app/models/auth/authenticate';
import { ILogin } from 'src/app/models/auth/interfaces/login.interface';
import { Login } from 'src/app/models/auth/login';
import { ExternalParameters } from 'src/app/models/settings/external-parameters';
import { ExternalResponse } from 'src/app/models/settings/external-response';
import { AuthService } from 'src/app/services/auth.service';
import { DialogService } from 'src/app/services/dialog.service';
import { SettingsService } from 'src/app/services/settings.service';
import { environment } from 'src/environments/environment';
import { FormFocusDirective } from '../../controls/directives/form-focus.directive';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    imports: [FormsModule, FormFocusDirective, ReactiveFormsModule, FaIconComponent, MatFormFieldModule, MatInputModule, MatButtonModule, MatTooltipModule, NgClass],
})
export class LoginComponent implements OnDestroy, OnInit {
    private settingsService = inject(SettingsService);
    private destroyRef = inject(DestroyRef);
    private toastrService = inject(ToastrService);
    private activatedRoute = inject(ActivatedRoute);

    public hidePasswordIcon = faEyeSlash;
    public showPasswordIcon = faEye;
    version: string = environment.version;

    // Délai de prévenance de l'expiration de mot de passe en jours
    private delayNotice: number = 15;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    // Indique si l'autentification a échoué
    public authenticationFailed = false;

    // Indique si l'utilisateur n'a pas les autorisations pour acceder à l'interface.
    public authorizationFailed = false;

    // Information du login
    public login: ILogin;

    // Nom de l'icone clé
    public faKey = faKey;

    // Valeur indiquant si le texte du champ de formulaire 'Mot de passe' est caché.
    public passwordTextHidden = true;

    public loginForm: UntypedFormGroup;

    /**
     * Valeur indiquant si une authentification est en cours.
     */
    private authenticationInProgress: boolean = false;

    /**
     * Constructeur
     */
    constructor(
        private router: Router,
        private authService: AuthService,
        private formBuilder: UntypedFormBuilder,
        private dialogService: DialogService,
        private spinner: NgxSpinnerService
    ) {
        console.log('login constructor');
        this.login = Login.createFormCopy(new Login());

        if (this.activatedRoute.snapshot.queryParams['response']) {
            const response = JSON.parse(this.activatedRoute.snapshot.queryParams['response']) as ExternalResponse;

            if (response) {
                if (response.code === 200) {
                    this.toastrService.success(response.message, '', { enableHtml: true });
                } else {
                    this.toastrService.warning(response.message, '', { enableHtml: true });
                }
                this.router.navigate([]);
            }
        }
    }

    // Initialise le composant
    ngOnInit(): void {
        this.loginForm = this.formBuilder.group({
            loginFormControl: ['', Validators.required],
            passwordFormControl: ['', Validators.required],
        });
    }

    /**
     * Détruit le composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }
    /**
     * appelé lors du clic sur le bouton connexion
     */
    public authenticate() {
        if (this.authenticationInProgress === false) {
            this.spinner.show('main-spinner');
            console.log('authenticate');
            this.authenticationInProgress = true;
            this.authenticationFailed = false;
            this.authorizationFailed = false;
            this.authService
                .logon(this.login)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe({
                    next: (authenticate) => {
                        this.authenticationInProgress = false;
                        this.checkPasswordExpiration(authenticate);

                        if (authenticate) {
                            console.log('Authentification done', authenticate);
                        } else {
                            this.authenticationFailed = false;
                            this.authorizationFailed = true;
                            this.passwordTextHidden = true;
                        }

                        this.spinner.hide('main-spinner');
                    },
                    error: (error: HttpErrorResponse | HttpError) => {
                        this.authenticationInProgress = false;
                        this.authenticationFailed = true;
                        this.authorizationFailed = false;
                        this.passwordTextHidden = true;
                        this.spinner.hide('main-spinner');

                        if (error instanceof HttpError && error.code == 409) {
                            console.log('Authentication return code 409');
                            this.dialogService.openForceConnection(this.login.login).subscribe((confirmation: boolean) => {
                                if (confirmation === true) {
                                    this.login.forceConnection = true;
                                    this.authenticate();
                                }
                            });
                        }
                    },
                });
        }
    }

    forgottenPassword() {
        this.spinner.show('main-spinner');

        this.settingsService
            .getSettings()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (x) => {
                    let gumUrl: string;
                    try {
                        gumUrl = x.apps['gum']['url'];
                    } catch {
                        gumUrl = null;
                    }

                    if (gumUrl) {
                        let parameters = new ExternalParameters();
                        parameters.name = 'Hub4B - Back Office';
                        parameters.return = `${window.location.origin}/login`;
                        window.location.href = `${gumUrl}/forgottenpassword?parameters=${JSON.stringify(parameters)}`;
                    } else {
                        this.toastrService.warning("Votre domaine n'est pas correctement configuré.<br />Veuillez contacter votre administrateur.", '', {
                            enableHtml: true,
                        });
                    }
                },
                error: () => {
                    this.toastrService.warning("Votre domaine n'est pas correctement configuré.<br />Veuillez contacter votre administrateur.", '', {
                        enableHtml: true,
                    });
                },
                complete: () => {
                    this.spinner.hide('main-spinner');
                },
            });
    }

    private checkPasswordExpiration(authenticate: Authenticate) {
        if (authenticate.token.passwordExpireOn) {
            const passwordExpireDate = new Date(authenticate.token.passwordExpireOn);

            if (passwordExpireDate.getFullYear() > 1) {
                let limiteDate = new Date(authenticate.token.passwordExpireOn);
                limiteDate.setDate(passwordExpireDate.getDate() - this.delayNotice);

                const today = new Date();

                if (today > limiteDate) {
                    this.toastrService.info(
                        `Votre mot de passe expire dans moins de ${this.DaysBetweenTwoDate(today, passwordExpireDate)} jour(s).<br />Veuillez réinitialiser votre mot de passe.`,
                        '',
                        {
                            enableHtml: true,
                        }
                    );
                }
            }
        }
    }

    DaysBetweenTwoDate(dateDebut: Date, dateFin: Date): number {
        const uneJourneeEnMillisecondes = 24 * 60 * 60 * 1000; // Nombre de millisecondes dans une journée
        const differenceEnMillisecondes = dateFin.getTime() - dateDebut.getTime();
        const nombreDeJours = Math.floor(differenceEnMillisecondes / uneJourneeEnMillisecondes);
        return nombreDeJours;
    }
}
